import React, { useState, useEffect, useCallback } from "react";
import { Flex, Typography, Input, Button, Popover, ColorPicker, message, Tooltip } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, TagsOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import orderTagService from "../../services/orderTagService";

const { Text } = Typography;

const DEFAULT_COLORS = [
  "#1890ff", "#52c41a", "#faad14", "#f5222d", "#722ed1",
  "#13c2c2", "#eb2f96", "#fa541c", "#a0d911", "#2f54eb",
];

const SidebarTags = ({ collapsed, onTagsLoaded }) => {
  const [tags, setTags] = useState([]);
  const [newTagName, setNewTagName] = useState("");
  const [newTagColor, setNewTagColor] = useState("#1890ff");
  const [addOpen, setAddOpen] = useState(false);
  const [editTag, setEditTag] = useState(null);
  const [editName, setEditName] = useState("");
  const [editColor, setEditColor] = useState("");
  const [highlightedUid, setHighlightedUid] = useState(null);
  const navigate = useNavigate();
  const { uid: activeUid } = useParams();

  const fetchTags = useCallback(async () => {
    const res = await orderTagService.getTags();
    if (res?.success) {
      setTags(res.data || []);
      if (onTagsLoaded) onTagsLoaded(res.data || []);
    }
  }, [onTagsLoaded]);

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  useEffect(() => {
    const handler = () => fetchTags();
    window.addEventListener("tag-assignment-changed", handler);
    return () => window.removeEventListener("tag-assignment-changed", handler);
  }, [fetchTags]);

  useEffect(() => {
    const handler = (e) => {
      const uid = e.detail?.uid;
      if (!uid) return;
      setHighlightedUid(uid);
      setTimeout(() => setHighlightedUid(null), 900);
    };
    window.addEventListener("tag-highlight", handler);
    return () => window.removeEventListener("tag-highlight", handler);
  }, []);

  const notifyTagListChanged = () => {
    window.dispatchEvent(new Event("tag-list-changed"));
  };

  const handleCreate = async () => {
    if (!newTagName.trim()) return;
    const res = await orderTagService.createTag(newTagName.trim(), newTagColor);
    if (res?.success) {
      message.success("Tag created");
      setNewTagName("");
      setNewTagColor("#1890ff");
      setAddOpen(false);
      fetchTags();
      notifyTagListChanged();
    } else {
      message.error(res?.message || "Failed to create tag");
    }
  };

  const handleUpdate = async () => {
    if (!editName.trim()) return;
    const res = await orderTagService.updateTag(editTag.uid, editName.trim(), editColor);
    if (res?.success) {
      message.success("Tag updated");
      setEditTag(null);
      fetchTags();
      notifyTagListChanged();
    } else {
      message.error(res?.message || "Failed to update tag");
    }
  };

  const handleDelete = async (uid) => {
    const res = await orderTagService.deleteTag(uid);
    if (res?.success) {
      message.success("Tag deleted");
      fetchTags();
      notifyTagListChanged();
    }
  };

  const addContent = (
    <Flex vertical gap={8} style={{ width: 220 }}>
      <Input
        placeholder="Tag name"
        value={newTagName}
        onChange={(e) => setNewTagName(e.target.value)}
        onPressEnter={handleCreate}
        autoFocus
      />
      <Flex gap={6} wrap="wrap">
        {DEFAULT_COLORS.map((c) => (
          <span
            key={c}
            onClick={() => setNewTagColor(c)}
            style={{
              width: 20, height: 20, borderRadius: "50%", backgroundColor: c,
              cursor: "pointer", border: newTagColor === c ? "2px solid #000" : "2px solid transparent",
              display: "inline-block",
            }}
          />
        ))}
      </Flex>
      <Flex justify="flex-end" gap={8}>
        <Button size="small" onClick={() => setAddOpen(false)}>Cancel</Button>
        <Button size="small" type="primary" onClick={handleCreate}>Create</Button>
      </Flex>
    </Flex>
  );

  const editContent = editTag && (
    <Flex vertical gap={8} style={{ width: 220 }}>
      <Input
        value={editName}
        onChange={(e) => setEditName(e.target.value)}
        onPressEnter={handleUpdate}
        autoFocus
      />
      <Flex gap={6} wrap="wrap">
        {DEFAULT_COLORS.map((c) => (
          <span
            key={c}
            onClick={() => setEditColor(c)}
            style={{
              width: 20, height: 20, borderRadius: "50%", backgroundColor: c,
              cursor: "pointer", border: editColor === c ? "2px solid #000" : "2px solid transparent",
              display: "inline-block",
            }}
          />
        ))}
      </Flex>
      <Flex justify="flex-end" gap={8}>
        <Button size="small" onClick={() => setEditTag(null)}>Cancel</Button>
        <Button size="small" type="primary" onClick={handleUpdate}>Save</Button>
      </Flex>
    </Flex>
  );

  if (collapsed) {
    return (
      <Tooltip title="My Tags" placement="right">
        <Flex justify="center" style={{ padding: "8px 0" }}>
          <TagsOutlined style={{ color: "rgba(255,255,255,0.65)", fontSize: 18 }} />
        </Flex>
      </Tooltip>
    );
  }

  return (
    <Flex vertical style={{ width: "100%", padding: "0 12px", marginTop: 8 }}>
      <Flex justify="space-between" align="center" style={{ marginBottom: 6 }}>
        <Text style={{ color: "rgba(255,255,255,0.45)", fontSize: 11, textTransform: "uppercase", letterSpacing: 1 }}>
          My Tags
        </Text>
        <Popover content={addContent} title="New Tag" trigger="click" open={addOpen} onOpenChange={setAddOpen} placement="rightTop">
          <PlusOutlined style={{ color: "rgba(255,255,255,0.65)", cursor: "pointer", fontSize: 13 }} />
        </Popover>
      </Flex>

      <Flex vertical gap={2}>
        {tags.map((tag) => (
          <Popover
            key={tag.uid}
            content={editContent}
            title="Edit Tag"
            trigger="click"
            open={editTag?.uid === tag.uid}
            onOpenChange={(open) => {
              if (!open) setEditTag(null);
            }}
            placement="rightTop"
          >
            <Flex
              align="center"
              justify="space-between"
              data-tag-uid={tag.uid}
              style={{
                padding: "5px 8px",
                borderRadius: 6,
                cursor: "pointer",
                background: highlightedUid === tag.uid
                  ? `${tag.color}44`
                  : activeUid === tag.uid
                  ? "rgba(255,255,255,0.15)"
                  : "transparent",
                transform: highlightedUid === tag.uid ? "scale(1.06)" : "scale(1)",
                transition: "background 0.25s, transform 0.25s, box-shadow 0.25s",
                boxShadow: highlightedUid === tag.uid ? `0 0 8px ${tag.color}88` : "none",
              }}
              className="sidebar-tag-item"
              onClick={() => navigate(`/order-management/tagged-orders/${tag.uid}`)}
            >
              <Flex align="center" gap={8} style={{ flex: 1, minWidth: 0 }}>
                <span style={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: tag.color, display: "inline-block", flexShrink: 0 }} />
                <Tooltip title={tag.name} mouseEnterDelay={0.5}>
                  <Text style={{ color: "rgba(255,255,255,0.85)", fontSize: 13, minWidth: 0 }} ellipsis>
                    {tag.name}
                  </Text>
                </Tooltip>
                {tag.order_count > 0 && (
                  <Text style={{ color: "rgba(255,255,255,0.45)", fontSize: 11, flexShrink: 0 }}>
                    {tag.order_count}
                  </Text>
                )}
              </Flex>
              <Flex gap={6} className="tag-actions" style={{ flexShrink: 0, marginLeft: 6 }} onClick={(e) => e.stopPropagation()}>
                <EditOutlined
                  style={{ color: "rgba(255,255,255,0.45)", fontSize: 12 }}
                  onClick={() => { setEditTag(tag); setEditName(tag.name); setEditColor(tag.color); }}
                />
                <DeleteOutlined
                  style={{ color: "rgba(255,255,255,0.45)", fontSize: 12 }}
                  onClick={() => handleDelete(tag.uid)}
                />
              </Flex>
            </Flex>
          </Popover>
        ))}
      </Flex>
    </Flex>
  );
};

export default SidebarTags;
